import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { previewImportJson, importJson } from '../api';
import type { ImportPreviewItem, ImportResult } from '../api';
import VehicleSearchField from './VehicleSearchField';
import { formatDate, formatDatetime } from '../utils/dates';
import { useSettings } from '../context/SettingsContext';

interface Props {
  onImported: () => void;
  onClose: () => void;
}

export default function ImportModal({ onImported, onClose }: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const tz = settings.timezone || 'Europe/Copenhagen';
  const inputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [rentals, setRentals] = useState<unknown[] | null>(null);
  const [filename, setFilename] = useState('');
  const [previewItems, setPreviewItems] = useState<ImportPreviewItem[]>([]);
  const [assignments, setAssignments] = useState<Record<string, { id: number; registration_number: string } | null>>({});
  const [results, setResults] = useState<ImportResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        const arr = Array.isArray(parsed) ? parsed : parsed?.results;
        if (!Array.isArray(arr) || arr.length === 0) throw new Error('Filen er ikke et gyldigt scraper-format');
        setRentals(arr);
        setError(null);
      } catch (err) {
        setError('Ugyldig JSON-fil: ' + (err as Error).message);
        setRentals(null);
      }
    };
    reader.readAsText(file, 'utf-8');
  };

  const handlePreview = async () => {
    if (!rentals) return;
    setLoading(true);
    setError(null);
    try {
      const { items } = await previewImportJson(rentals);
      setPreviewItems(items);
      const init: Record<string, { id: number; registration_number: string } | null> = {};
      for (const item of items) {
        if (item.status === 'new' && item.rental_id) {
          init[item.rental_id] = item.vehicle_id && item.registration_number
            ? { id: item.vehicle_id, registration_number: item.registration_number }
            : null;
        }
      }
      setAssignments(init);
      setStep(2);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!rentals) return;
    setLoading(true);
    setError(null);
    try {
      const vehicleIds: Record<string, number | null> = {};
      for (const [rid, v] of Object.entries(assignments)) vehicleIds[rid] = v?.id ?? null;
      const { results: res } = await importJson(rentals, vehicleIds);
      setResults(res);
      if (res.some(r => r.status === 'imported' || r.status === 'merged' || r.status === 'updated')) onImported();
      setStep(3);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally {
      setLoading(false);
    }
  };

  const assign = (rentalId: string, vehicle: { id: number; registration_number: string } | null) =>
    setAssignments(prev => ({ ...prev, [rentalId]: vehicle }));

  const handleReset = () => {
    setStep(1);
    setRentals(null);
    setFilename('');
    setPreviewItems([]);
    setAssignments({});
    setResults([]);
    setError(null);
  };

  const newItems = previewItems.filter(i => i.status === 'new' || i.status === 'match' || i.status === 'update');
  const importedCount = results.filter(r => r.status === 'imported' || r.status === 'merged' || r.status === 'updated').length;

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      new:       'bg-sage-100 text-sage-700',
      match:     'bg-sage-100 text-sage-700',
      update:    'bg-rose-100 text-rose-700',
      duplicate: 'bg-cream-200 text-ink-500',
      imported:  'bg-sage-100 text-sage-700',
      merged:    'bg-sage-100 text-sage-700',
      updated:   'bg-rose-100 text-rose-700',
      error:     'bg-rose-100 text-rose-700',
    };
    const labels: Record<string, string> = {
      new:       t('rental.status_new'),
      match:     t('rental.status_match'),
      update:    t('rental.status_update'),
      duplicate: t('rental.status_duplicate'),
      imported:  t('rental.status_imported'),
      merged:    t('rental.status_merged'),
      updated:   t('rental.status_updated'),
      error:     t('rental.status_error'),
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[status] ?? 'bg-gray-100 text-gray-500'}`}>
        {labels[status] ?? status}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('rental.import_title')}</h2>
            <div className="flex gap-4 mt-2 text-xs">
              {([1, 2, 3] as const).map(s => (
                <span key={s} className={step === s ? 'text-[var(--color-primary)] font-medium' : 'text-gray-400'}>
                  {s}. {s === 1 ? t('rental.step_select') : s === 2 ? t('rental.step_review') : t('rental.step_done')}
                </span>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">

          {/* ── STEP 1: SELECT FILE ── */}
          {step === 1 && (
            <>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[var(--color-border-focus)] transition-colors"
                onClick={() => inputRef.current?.click()}
                onDrop={e => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    const ev = { target: { files: e.dataTransfer.files } } as unknown as React.ChangeEvent<HTMLInputElement>;
                    handleFile(ev);
                  }
                }}
                onDragOver={e => e.preventDefault()}
              >
                <p className="text-sm text-gray-500">{t('rental.drop_json_hint')}</p>
                <p className="text-xs text-gray-400 mt-1">{t('rental.drop_json_hint_sub')}</p>
                <input ref={inputRef} type="file" accept=".json,application/json" className="hidden"
                  onChange={handleFile} />
              </div>

              {rentals && (
                <div className="bg-[var(--color-success-100)] border border-green-200 rounded-lg px-4 py-3 flex items-center gap-3">
                  <span className="text-[var(--color-success)] font-medium text-sm">{filename}</span>
                  <span className="text-xs text-[var(--color-success)]">{t('rental.json_rental_count', { count: rentals.length })}</span>
                </div>
              )}

              {error && <p className="text-sm text-[var(--color-error)]">{error}</p>}
            </>
          )}

          {/* ── STEP 2: PREVIEW ── */}
          {step === 2 && (
            <>
              <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-1">
                <span>{t('rental.status_new')}: <strong className="text-gray-800">{previewItems.filter(i => i.status === 'new').length}</strong></span>
                <span>{t('rental.status_match')}: <strong className="text-gray-800">{previewItems.filter(i => i.status === 'match').length}</strong></span>
                {previewItems.some(i => i.status === 'update') && (
                  <span>{t('rental.status_update')}: <strong className="text-gray-800">{previewItems.filter(i => i.status === 'update').length}</strong></span>
                )}
                {previewItems.some(i => i.status === 'duplicate') && (
                  <span>{t('rental.status_duplicate')}: <strong className="text-gray-800">{previewItems.filter(i => i.status === 'duplicate').length}</strong></span>
                )}
                {previewItems.some(i => i.status === 'error') && (
                  <span className="text-[var(--color-error)]">{t('rental.status_error')}: <strong>{previewItems.filter(i => i.status === 'error').length}</strong></span>
                )}
              </div>

              <ul className="space-y-2">
                {previewItems.map(item => (
                  <li key={item.rental_id} className="border rounded-lg p-3 text-sm">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {statusBadge(item.status)}
                      {item.rental_id && (
                        <span className="text-xs text-gray-400 font-mono">#{item.rental_id}</span>
                      )}
                      {item.renter_name && (
                        <span className="text-xs font-medium text-gray-700">{item.renter_name}</span>
                      )}
                      {item.starts_at && item.ends_at && (
                        <span className="text-xs text-gray-500">
                          {formatDatetime(item.starts_at, tz)} → {formatDate(item.ends_at)}
                        </span>
                      )}
                      {item.payout != null && (
                        <span className="text-xs text-gray-600 ml-auto">{Number(item.payout).toLocaleString('da-DK')} DKK</span>
                      )}
                    </div>

                    {item.status === 'new' && (
                      <div className="mt-1">
                        {assignments[item.rental_id] ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[var(--color-success)]">✓ {assignments[item.rental_id]!.registration_number}</span>
                            <button
                              className="text-xs text-gray-400 hover:text-orange-500 underline"
                              onClick={() => assign(item.rental_id, null)}>
                              {t('rental.reassign')}
                            </button>
                          </div>
                        ) : (
                          <div>
                            <p className="text-xs text-orange-500 mb-1">{t('rental.vehicle_not_matched')} ({item.registration_number ?? '—'})</p>
                            <VehicleSearchField
                              placeholder={t('rental.search_vehicle')}
                              onSelect={v => assign(item.rental_id, v)}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {item.status === 'duplicate' && item.existing_id && (
                      <p className="text-xs text-gray-400 mt-0.5">ID #{item.existing_id}</p>
                    )}

                    {item.status === 'error' && item.error && (
                      <p className="text-xs text-[var(--color-error)] mt-0.5">{item.error}</p>
                    )}
                  </li>
                ))}
              </ul>

              {error && <p className="text-sm text-[var(--color-error)]">{error}</p>}
            </>
          )}

          {/* ── STEP 3: RESULTS ── */}
          {step === 3 && (
            <>
              <p className="text-sm text-gray-600">
                {t('rental.import_done', { count: importedCount, total: results.length })}
              </p>
              <ul className="space-y-2">
                {results.map((r, i) => (
                  <li key={i} className="border rounded-lg p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {statusBadge(r.status)}
                      <span className="text-xs font-mono text-gray-400">#{r.rental_id}</span>
                    </div>
                    {r.error && <p className="text-xs text-[var(--color-error)] mt-1">{r.error}</p>}
                  </li>
                ))}
              </ul>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between">
          <div>
            {step === 2 && newItems.some(i => assignments[i.rental_id] == null) && (
              <p className="text-xs text-gray-400">{t('rental.import_without_vehicle')}</p>
            )}
          </div>
          <div className="flex gap-3">
            {step === 3 && (
              <button onClick={handleReset} className="btn btn-secondary">
                {t('rental.new_file')}
              </button>
            )}
            <button onClick={onClose} className="btn btn-secondary">{t('modal.close')}</button>
            {step === 1 && (
              <button
                onClick={handlePreview}
                disabled={!rentals || loading}
                className="btn btn-primary disabled:opacity-50">
                {loading ? t('rental.importing') : t('rental.parse_json_btn')}
              </button>
            )}
            {step === 2 && (
              <button
                onClick={handleImport}
                disabled={loading || newItems.length === 0}
                className="btn btn-primary disabled:opacity-50">
                {loading ? t('rental.importing') : t('rental.import_btn', { count: newItems.length })}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
